import { GeoFilterInput, PersonFilterInput } from '~/types/api.graphql';
import { createGeoFilterHelper, createGeoFilterHelperFromGraphQL } from '~/utils/filters';

/**
 * The geo filters of an agent- or API-provided filter list, rebuilt into clean PersonFilterInput.
 * Only geo filters narrow a Pulse selection (as in the non-agent flow), so they are pulled out of
 * compound groups, the shape stored on Pulse audiences. Rebuilding each one through the geo filter
 * helpers drops any extra field (`__typename`, a stray key from the agent) that would make GraphQL
 * reject the whole count query; an entry that isn't a recognisable geo filter is skipped.
 */
export const sanitizePulseFilters = (value: unknown): PersonFilterInput[] =>
  collectGeoFilters(value).flatMap((geoFilter) => {
    try {
      const parsed = createGeoFilterHelperFromGraphQL(geoFilter).fromGraphQLGeoFilter();
      return [createGeoFilterHelper(parsed).toGraphQLGeoFilter()];
    } catch {
      return [];
    }
  });

const collectGeoFilters = (value: unknown): GeoFilterInput[] =>
  (Array.isArray(value) ? value : []).flatMap((entry) => {
    if (!isRecord(entry)) return [];
    if (isGeoFilter(entry.geoFilter)) return [entry.geoFilter];
    return isRecord(entry.compoundFilter) ? collectGeoFilters(entry.compoundFilter.filters) : [];
  });

const isGeoFilter = (value: unknown): value is GeoFilterInput => {
  const operator = isRecord(value) ? value.operator : undefined;
  return isRecord(operator) && !!(operator.polygon || operator.in || operator.distance);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
